import { memo, useCallback, useEffect, useRef, useState } from 'react';
import './styles/SnowCanvas.css';
import { Box } from 'grommet';

export const SnowCanvas = memo(({ intensity, fallSpeed, wind, snowSize, accumulate, maxDriftHeight, meltSimulation, meltSpeed, weatherSim }) => {

  // --- Engine Refs (Mutable state for the game loop) ---
  const canvasRef = useRef(null);
  const requestRef = useRef(null);
  const containerRef = useRef(null); 
  const driftCanvasRef = useRef(document.createElement('canvas')); 
  
  // Game State
  const gameState = useRef({
    width: 0,
    height: 0,
    grid: [],
    heightMap: [],
    cols: 0,
    rows: 0,
    snowflakes: [],
    spawnAccumulator: 0, // NEW: Allows fractional spawning
    cellSize: 4,
    meltSystem: {
      isMelting: false, 
      timer: 0,
      phaseDuration: 300 
    },
    // NEW: Weather Simulation State
    weather: {
      phase: 'calm',
      timer: 0,
      duration: 300,
      // Current interpolated values (smooth transitions)
      current: { spawnRate: 0, wind: 0, gravity: 1.5 },
      // Target values to reach
      target: { spawnRate: 0, wind: 0, gravity: 1.5 }
    }
  });

  // Settings Ref (Syncs Props to Game Loop)
  const settingsRef = useRef({
    spawnRate: 5,
    maxParticles: 15000, 
    wind: 0,
    gravity: 1.5,
    maxSize: 3,
    accumulate: true,
    maxHeightPct: 0.5,
    meltSpeed: 0,
    meltSimulation: true,
    weatherSim: false
  });

  // Sync Props to Settings Ref whenever they change
  useEffect(() => {
    // FIX: Scale spawn rate by gravity (fallSpeed).
    const gravityFactor = Math.max(fallSpeed, 0.1) / 1.5;
    
    settingsRef.current = {
      spawnRate: (intensity * 0.2) * gravityFactor, 
      maxParticles: 15000,
      wind: wind,
      gravity: fallSpeed,
      maxSize: snowSize,
      accumulate: accumulate,
      maxHeightPct: maxDriftHeight / 100,
      meltSpeed: meltSpeed,
      meltSimulation: meltSimulation,
      weatherSim: weatherSim
    };
  }, [intensity, fallSpeed, wind, snowSize, accumulate, maxDriftHeight, meltSpeed, meltSimulation, weatherSim ]);

  // --- Logic: Grid & Resizing ---
  const initGrid = useCallback(() => {
    const state = gameState.current;
    if (!state.cols || !state.rows || state.cols <= 0 || state.rows <= 0) return;

    state.grid = new Array(state.cols).fill(null).map(() => new Array(state.rows).fill(null));
    
    const driftCanvas = driftCanvasRef.current;
    if (driftCanvas && driftCanvas.width > 0 && driftCanvas.height > 0) {
        const driftCtx = driftCanvas.getContext('2d', { alpha: true });
        driftCtx.clearRect(0, 0, state.width, state.height);
    }
    
    state.heightMap = new Array(state.cols).fill(0);
    const offset1 = Math.random() * 100;
    const offset2 = Math.random() * 100;
    for (let x = 0; x < state.cols; x++) {
        let val = Math.sin(x * 0.02 + offset1) * 8; 
        val += Math.sin(x * 0.05 + offset2) * 3;
        state.heightMap[x] = Math.floor(val);
    }
  }, []);

  const handleResize = useCallback(() => {
    window.requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      const driftCanvas = driftCanvasRef.current;
      const state = gameState.current;
      const container = containerRef.current;

      if (!container) return;

      const rect = container.getBoundingClientRect();
      state.width = rect.width || window.innerWidth;
      state.height = rect.height || window.innerHeight;

      if (canvas) {
        canvas.width = state.width;
        canvas.height = state.height;
      }
      if (driftCanvas) {
        driftCanvas.width = state.width;
        driftCanvas.height = state.height;
      }

      if (state.width === 0 || state.height === 0 || state.cellSize === 0) {
          state.cols = 0;
          state.rows = 0;
      } else {
          state.cols = Math.ceil(state.width / state.cellSize);
          state.rows = Math.ceil(state.height / state.cellSize);
      }

      initGrid();
    });
  }, [initGrid]);

  // --- Helper: Lerp ---
  const lerp = (start, end, amt) => {
    return (1 - amt) * start + amt * end;
  };

  const getRandom = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  // --- Logic: Entities ---
  const createSnowflake = (activeSettings) => {
    const state = gameState.current;
    
    const baseR = 240, baseG = 248, baseB = 255;
    const noiseIntensity = 20;
    const rOffset = (Math.random() * 2 - 1) * noiseIntensity; 
    const gOffset = (Math.random() * 2 - 1) * noiseIntensity;
    const bOffset = (Math.random() * 2 - 1) * noiseIntensity;
    const r = Math.max(0, Math.min(255, Math.floor(baseR + rOffset)));
    const g = Math.max(0, Math.min(255, Math.floor(baseG + gOffset)));
    const b = Math.max(0, Math.min(255, Math.floor(baseB + bOffset)));
    
    const z = Math.random() * 0.5 + 0.5;

    return {
      x: Math.random() * state.width,
      y: -10,
      z: z,
      size: Math.random() * activeSettings.maxSize * z,
      speedMult: 0.8 + Math.random() * 0.4,
      oscillationSpeed: Math.random() * 0.05 + 0.01,
      oscillationOffset: Math.random() * Math.PI * 2,
      vx: 0, vy: 0, dead: false,
      color: `rgba(${r}, ${g}, ${b}, ${0.9 * z})`,
      driftColor: `rgba(${r}, ${g}, ${b}, 0.95)`
    };
  };

  // --- Main Loop ---
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    const driftCanvas = driftCanvasRef.current;
    const state = gameState.current;
    
    // Determine effective settings (UI vs Weather Sim)
    let activeSettings = { ...settingsRef.current };
    
    if (activeSettings.weatherSim) {
        const w = state.weather;
        w.timer++;

        // State Machine
        if (w.timer > w.duration) {
            w.timer = 0;
            // Pick next phase
            const phases = ['calm', 'light', 'medium', 'storm'];
            const next = phases[Math.floor(Math.random() * phases.length)];
            w.phase = next;
          
            // Set Targets based on phase
            if (next === 'calm') {
                w.target = { spawnRate: 0, wind: 0, gravity: 1.0 };
                w.duration = getRandom(60, 300) * 60; // 1-5 mins
            } else if (next === 'light') {
                w.target = { spawnRate: 0.1, wind: (Math.random() - 0.5), gravity: 0.5 };
                w.duration = getRandom(900, 1800) * 60; // 15-30 mins
            } else if (next === 'medium') {
                w.target = { spawnRate: 1, wind: (Math.random() - 0.5) * 3, gravity: 1 };
                w.duration = getRandom(60, 120) * 60; // 1-2 mins
            } else if (next === 'storm') {
                w.target = { spawnRate: 5, wind: (Math.random() < 0.5 ? -4 : 4), gravity: 2 };
                w.duration = getRandom(30, 60) * 60; // 30-60 sec.
            }
        }

        // Smooth Interpolation
        const lerpFactor = 0.005; // Very slow smooth change
        w.current.spawnRate = lerp(w.current.spawnRate, w.target.spawnRate, lerpFactor);
        w.current.wind = lerp(w.current.wind, w.target.wind, lerpFactor);
        w.current.gravity = lerp(w.current.gravity, w.target.gravity, lerpFactor);

        // FIX: SNAP TO ZERO
        // If the target is zero and we are close, force it to zero.
        // This prevents the "infinite asymptotic approach" where it stays at 0.0001
        if (w.target.spawnRate === 0 && w.current.spawnRate < 0.1) {
            w.current.spawnRate = 0;
        }

        // Apply to active settings
        activeSettings.spawnRate = w.current.spawnRate;
        activeSettings.wind = w.current.wind;
        activeSettings.gravity = w.current.gravity;
    } else {
        const w = state.weather;
        w.current.spawnRate = activeSettings.spawnRate;
        w.current.wind = activeSettings.wind;
        w.current.gravity = activeSettings.gravity;
    }

    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    ctx.imageSmoothingEnabled = false;

    ctx.clearRect(0, 0, state.width, state.height);

    if (driftCanvas.width > 0 && driftCanvas.height > 0) {
        ctx.drawImage(driftCanvas, 0, 0);
    }
    
    if (state.cols <= 0 || state.rows <= 0 || !state.grid || state.grid.length === 0) {
        requestRef.current = requestAnimationFrame(animate);
        return;
    }

    const driftCtx = driftCanvas.getContext('2d');

    // Melting Cycle

    if (activeSettings.meltSimulation) {
      const meltSys = state.meltSystem;
      meltSys.timer++;
      if (meltSys.timer >= meltSys.phaseDuration) {
          meltSys.timer = 0;
          meltSys.isMelting = !meltSys.isMelting;
          meltSys.phaseDuration = getRandom(600, 900); // 10-15 mins
      }
    } else {
      state.meltSystem.isMelting = true;
    }
  

    if (activeSettings.meltSpeed > 0 && state.meltSystem.isMelting) {
      for (let i = 0; i < activeSettings.meltSpeed; i++) {
        const c = Math.floor(Math.random() * state.cols);
        let bestCol = -1;
        let highestPeakY = state.rows + 1; 
        const radius = 2; 
        
        for (let offset = -radius; offset <= radius; offset++) {
            let targetC = c + offset;
            if (targetC >= 0 && targetC < state.cols && state.grid[targetC]) {
                for (let y = 0; y < state.rows; y++) {
                    if (state.grid[targetC][y] === 1) {
                        if (y < highestPeakY) {
                            highestPeakY = y;
                            bestCol = targetC;
                        }
                        break; 
                    }
                }
            }
        }
        if (bestCol !== -1 && highestPeakY < state.rows) {
            state.grid[bestCol][highestPeakY] = null;
            driftCtx.clearRect(bestCol * state.cellSize, highestPeakY * state.cellSize, state.cellSize, state.cellSize);
        }
      }
    }

    // Spawn (With Accumulator for fractional rates)
    // FIX: Only accumulate if rate is significant
    if (activeSettings.spawnRate > 0.01) {
        state.spawnAccumulator += activeSettings.spawnRate;
        const toSpawn = Math.floor(state.spawnAccumulator);
        state.spawnAccumulator -= toSpawn;

        for (let i = 0; i < toSpawn; i++) {
          if (state.snowflakes.length < activeSettings.maxParticles) {
            state.snowflakes.push(createSnowflake(activeSettings));
          }
        }
    } else {
        // Reset if we are effectively 0 to avoid building up phantom snow
        state.spawnAccumulator = 0;
    }

    // Update
    for (let i = state.snowflakes.length - 1; i >= 0; i--) {
      const flake = state.snowflakes[i];
      flake.vy = (activeSettings.gravity * flake.z * flake.speedMult);
      let flutterAmp = Math.min(1.5, Math.max(0.1, activeSettings.gravity * 0.4));
      flake.vx = (activeSettings.wind * flake.z) + Math.sin(flake.y * 0.01 + flake.oscillationOffset) * flutterAmp;
      flake.x += flake.vx;
      flake.y += flake.vy;

      if (flake.x > state.width) flake.x = 0;
      if (flake.x < 0) flake.x = state.width;

      let gx = Math.floor(flake.x / state.cellSize);
      let gy = Math.floor(flake.y / state.cellSize);
      let landed = false;

      if (activeSettings.accumulate) {
        if (gx >= 0 && gx < state.cols && state.grid[gx]) {
            const cellBelow = (gy + 1 < state.rows) ? state.grid[gx][gy + 1] : 1;
            if (gy >= state.rows - 1 || cellBelow === 1) {
                if (gy >= state.rows - 1) {
                    landed = true;
                } else {
                    let canLeft = false;
                    let canRight = false;
                    if (gx > 0 && state.grid[gx - 1]) canLeft = (state.grid[gx - 1][gy + 1] === null);
                    if (gx < state.cols - 1 && state.grid[gx + 1]) canRight = (state.grid[gx + 1][gy + 1] === null);

                    if (canLeft && canRight) Math.random() < 0.5 ? flake.x -= state.cellSize : flake.x += state.cellSize;
                    else if (canLeft) flake.x -= state.cellSize;
                    else if (canRight) flake.x += state.cellSize;
                    else {
                        const baseLimit = state.rows * (1 - activeSettings.maxHeightPct);
                        const localLimit = baseLimit + (state.heightMap[gx] || 0);
                        if (gy >= localLimit) landed = true;
                        else flake.dead = true; 
                    }
                }
                if (landed) {
                    if (gy >= 0 && gy < state.rows) {
                        state.grid[gx][gy] = 1;
                        driftCtx.fillStyle = flake.driftColor;
                        driftCtx.fillRect(gx * state.cellSize, gy * state.cellSize, state.cellSize, state.cellSize);
                    }
                    flake.dead = true;
                }
            }
        }
      } else {
        if (flake.y > state.height) flake.dead = true;
      }

      if (flake.dead) {
        state.snowflakes.splice(i, 1);
      } else {
        const pixelSize = Math.max(1, Math.floor(flake.size));
        ctx.fillStyle = flake.color;
        ctx.fillRect(Math.floor(flake.x), Math.floor(flake.y), pixelSize, pixelSize);
      }
    }

    requestRef.current = requestAnimationFrame(animate);
  }, []); // Added weatherSim dependency

  // --- Start Up ---
  useEffect(() => {
    // Initial resize to set up canvas/grid
    handleResize();
    const observer = new ResizeObserver(() => {
        handleResize();
    });

    if (containerRef.current) {
        observer.observe(containerRef.current);
    }
    
    // Start Loop
    requestRef.current = requestAnimationFrame(animate);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(requestRef.current);
    };
  }, [animate, handleResize]);

    return (
        <Box fill ref={containerRef} background={'transparent'}>
            <canvas id="snowCanvas" ref={canvasRef} style={{ imageRendering: 'pixelated' }}></canvas>
        </Box>
    )
})
